import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLayout } from '@vimeo/clip-ui/hooks/use-layout';
import { useGetCommentsCount } from '@vimeo/comments-view';
import { useViewer } from '@vimeo/viewer';

import { useAlbumStore } from '../state/albumStore';
import { bpLikeButton, bpShareButton } from '../utils/analytics';
import { VideoActionBarModuleProps } from '../utils/types';
import { useGetBpSessionId } from './use-get-bp-session-id';
import { useLoginModal } from './use-login-modal';
import { useVideoLike } from './use-video-like';

/**
 * Hook to manage the video action bar module state and behavior.
 * @param canInteract - Whether the user can interact with the video.
 * @param clipId - The ID of the video clip.
 * @param clipRequestId - The request ID for the clip.
 * @param showComments - Whether to show the comments section.
 * @param shareModalActive - Whether the share modal is currently active.
 * @param showShare - Whether to show the share button.
 * @param setShareModalActive - Function to set the share modal active state.
 * @param showLike - Whether to show the like button.
 * @param canLike - Whether the user can like the video.
 * @param isVideoProcessing - Whether the video is currently being processed.
 * @returns An object containing action bar display configuration, handlers, public comments count, and a flag indicating if the action bar should be shown.
 */
export const useVideoActionBarModule = ({
  canInteract,
  clipId,
  clipRequestId,
  showComments,
  showDownload,
  shareModalActive,
  showShare,
  setShareModalActive,
  showLike,
  showAskAi,
  canLike,
  isVideoProcessing,
  setReportModalOpen,
}: VideoActionBarModuleProps & {
  shareModalActive: boolean;
  setShareModalActive: Dispatch<SetStateAction<boolean>>;
  setReportModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const viewer = useViewer();
  const sessionId = useGetBpSessionId();
  const {
    albumId,
    isOffsite,
    isShowcaseEmbedded,
    showcaseId,
    albumPrivacy,
    seoAllowIndexed,
    noOfVideos,
  } = useAlbumStore((state) => ({
    albumId: state.albumId,
    isOffsite: state.isOffsite,
    isShowcaseEmbedded: state.isShowcaseEmbedded,
    showcaseId: state.albumId,
    seoAllowIndexed: state.seoAllowIndexed,
    noOfVideos: state.noOfVideos,
    albumPrivacy: state.albumPrivacy,
  }));
  const { toggleLoginModal, setRedirectUrl } = useLoginModal();
  const {
    activeMobileDrawer,
    setActiveMobileDrawer,
    activeSideModule,
    onActiveDrawerModuleChange,
  } = useLayout();
  const { commentsCount: publicCommentsCount } = useGetCommentsCount(
    clipRequestId,
    true,
    undefined,
    showcaseId,
  );
  const shareModalOpenedRef = useRef(false);
  const [activeDownloadClipId, setActiveDownloadClipId] = useState<string | null>(null);

  const {
    added: isLiked,
    update: updateLike,
    totalLikes,
    isLikeLoading,
  } = useVideoLike({
    clipId: clipId,
    clipRequestId: clipRequestId,
    canLike,
  });

  const isLoggedIn = Boolean(viewer?.user);

  const bpCollectionContext = useMemo(
    () => ({
      entity_id: albumId,
      number_of_videos: noOfVideos,
      is_seo_on: seoAllowIndexed,
      collection_privacy: albumPrivacy,
    }),
    [albumId, noOfVideos, seoAllowIndexed, albumPrivacy],
  );

  const onDownloadClick = useCallback(
    () => setActiveDownloadClipId(clipId),
    [clipId, setActiveDownloadClipId],
  );

  const onLoginRequiredInShareModal = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.append('open_share', 'true');
    setRedirectUrl(url.toString());
    setShareModalActive(false);
    toggleLoginModal(true);
  }, [setRedirectUrl, setShareModalActive, toggleLoginModal]);

  // COMMENTS - MOBILE ONLY
  const onCommentClick = useCallback(() => {
    setActiveMobileDrawer(activeMobileDrawer === 'COMMENTS' ? '' : 'COMMENTS');
    if (shareModalActive) {
      setShareModalActive(false);
    }
  }, [activeMobileDrawer, setActiveMobileDrawer, setShareModalActive, shareModalActive]);

  const onShareClick = useCallback(() => {
    setShareModalActive((prev) => !prev);
    if (!shareModalActive) {
      bpShareButton(clipId, sessionId, bpCollectionContext, viewer);
    }
  }, [setShareModalActive, shareModalActive, clipId, sessionId, bpCollectionContext, viewer]);

  const onLikeClick = useCallback(async () => {
    if (!isLoggedIn) {
      setRedirectUrl(window.location.pathname + window.location.search);
      toggleLoginModal(true);
      return;
    }
    bpLikeButton(clipId, sessionId, bpCollectionContext, viewer);
    await updateLike();
  }, [
    isLoggedIn,
    clipId,
    sessionId,
    bpCollectionContext,
    viewer,
    updateLike,
    setRedirectUrl,
    toggleLoginModal,
  ]);

  const onReportClick = useCallback(() => {
    if (!isLoggedIn) {
      setRedirectUrl(window.location.pathname + window.location.search);
      toggleLoginModal(true);
      return;
    }
    setReportModalOpen(true);
  }, [isLoggedIn, setRedirectUrl, toggleLoginModal, setReportModalOpen]);

  const onReportClose = () => {
    setReportModalOpen(false);
  };

  const onAskAIClick = useCallback(() => {
    onActiveDrawerModuleChange(activeSideModule === 'VIMEO_AI' ? '' : 'VIMEO_AI');
  }, [activeSideModule, onActiveDrawerModuleChange]);

  const actionBarDisplayConfig = {
    showComments,
    showDownload,
    showLike: showLike,
    showShare: showShare,
    showVimeoAI: showAskAi,
    showWatchlater: false,
    showReport: true,
    showAnimatedLike: true,
  };

  const actionBarStateConfig = {
    likedClip: isLiked,
    totalLikes: totalLikes ?? 0,
    canLike: !isLoggedIn || canLike,
    isLikeLoading: isLikeLoading,
  };

  const actionBarHandlers = {
    onCommentClick,
    onDownloadClick,
    onShareClick,
    onLikeClick,
    onAskAIClick,
    onReportClick,
    onReportClose,
  };

  const shouldShowActionBar =
    canInteract &&
    !isVideoProcessing &&
    !isShowcaseEmbedded &&
    !isOffsite &&
    Object.values(actionBarDisplayConfig).some((item) => Boolean(item));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldOpenShareModal = params.get('open_share');
    if (!shareModalActive && !shareModalOpenedRef.current && shouldOpenShareModal === 'true') {
      shareModalOpenedRef.current = true;
      onShareClick();
      params.delete('open_share');
    }
  }, [shareModalActive, onShareClick]);

  return {
    actionBarStateConfig,
    actionBarDisplayConfig,
    actionBarHandlers,
    activeDownloadClipId,
    publicCommentsCount,
    setActiveDownloadClipId,
    shouldShowActionBar,
    onShareClick,
    onLoginRequiredInShareModal,
  };
};
